/**
 * WaitingPhaseView
 *
 * Two-week-wait surface shown in CycleDetailScreen for transfer-bearing
 * cycles after the user clicks "Enter Waiting Phase". Replaces the
 * MoonHubCompletionView while the cycle is in the luteal phase and no
 * outcome has been recorded yet.
 *
 * A single glass card: a title with the (assumed, editable) test date inline,
 * a progress ring, and the "Record beta result" / "Chat about this" actions.
 *
 * Not rendered on an FET cycle: that Outcome tab keeps one design, the step's
 * own panel. The clinic beta card this used to carry moved out with it, to
 * `FETBetaResultSeries`.
 * Reassurance and learn-while-you-wait content moved to the home page so Cycle
 * Detail stays focused on cycle data and entry.
 */

import { useEffect, useMemo, useRef } from "react";
import { Animated, View, Pressable, Alert } from "react-native";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import { CompactActionButton } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import Svg, {
  Circle,
  Defs,
  Image as SvgImage,
  Pattern,
  RadialGradient as SvgRadialGradient,
  Stop,
} from "react-native-svg";
import { Heart, Sparkles, MessageCircle, Camera } from "lucide-react-native";
import { EMBER } from "../../../../../onboarding/theme";
import { toLocalDateString } from "@src/packages/utils/date";
import StatusBadge from "../../../../components/StatusBadge";
import type { StepPerspective } from "@src/packages/homepage/sections/TreatmentProgressModal";
import {
  useTranslations,
  useLocale,
  type TranslationFunction,
  type Locale,
} from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

// ── Helpers ──────────────────────────────────────────────────

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysBetween(start: string, end: string): number {
  const a = new Date(start + "T00:00:00").getTime();
  const b = new Date(end + "T00:00:00").getTime();
  return Math.round((b - a) / MS_PER_DAY);
}

function todayStr(): string {
  // Local-tz formatted date — toISOString() converts to UTC, which can
  // shift the day for users east of UTC and produce off-by-one countdowns.
  return toLocalDateString(new Date());
}

// Map our i18n Locale codes to BCP-47 tags Intl understands.
function intlLocale(locale: Locale): string {
  return locale === "fr" ? "fr-FR" : "en-US";
}

function formatLongDate(iso: string | null, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: "short",
    month: "long",
    day: "numeric",
  }).format(d);
}

// ── Component ────────────────────────────────────────────────

interface Props {
  lutealStartDate: string | null; // YYYY-MM-DD
  testDate: string | null; // YYYY-MM-DD
  onTestDateChange?: (date: string) => void;
  /** Tapping the pregnancy-test row opens the add-appointment flow pre-filled
   *  as a beta hCG test, instead of a bare date picker. */
  onEditTestAppointment?: () => void;
  onRecordResult: () => void;
  /** Opens the AI chat primed to talk through the two-week wait. */
  onChatAboutThis?: () => void;
  /** Test-day-only secondary CTA. Optional — when omitted the button is a
   *  no-op placeholder ("coming soon"). Real photo OCR upload is Phase 4b. */
  onUploadResult?: () => void;
  isSavingTestDate?: boolean;
  testDateSaveError?: string | null;
  onRetryTestDate?: () => void;
  isLoadingResult?: boolean;
  /** Male-partner perspective override. When "partner", the adaptive copy
   *  and tips list are rewritten to address the AMAB partner of an AFAB
   *  primary patient. Otherwise canonical first-person copy is used. */
  perspective?: StepPerspective;
  /** Partner's first name (or fallback) — used in partner-perspective copy. */
  partnerDisplayName?: string;
}

export function WaitingPhaseView({
  lutealStartDate,
  testDate,
  onEditTestAppointment,
  onRecordResult,
  onChatAboutThis,
  onUploadResult,
  isSavingTestDate = false,
  testDateSaveError,
  onRetryTestDate,
  isLoadingResult = false,
  perspective = "self",
  partnerDisplayName,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("WaitingPhaseView");
  const { locale } = useLocale();
  const ns = "care.treatment_hub.waiting_phase";
  const testDateIsEditable = Boolean(onEditTestAppointment);

  const today = useMemo(() => todayStr(), []);

  const { dayCurrent, dayTotal, progress, isTestDay, isPastTest } =
    useMemo(() => {
      if (!lutealStartDate || !testDate) {
        return {
          dayCurrent: null as number | null,
          dayTotal: null as number | null,
          progress: 0,
          isTestDay: false,
          isPastTest: false,
        };
      }
      const total = Math.max(1, daysBetween(lutealStartDate, testDate));
      const elapsed = Math.max(0, daysBetween(lutealStartDate, today));
      const current = Math.min(total, elapsed + 1);
      return {
        dayCurrent: current,
        dayTotal: total,
        progress: Math.min(1, current / total),
        isTestDay: today === testDate,
        isPastTest: today > testDate,
      };
    }, [lutealStartDate, testDate, today]);

  // Fade-in on mount.
  const fade = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(fade, {
      toValue: 1,
      duration: 320,
      useNativeDriver: true,
    }).start();
  }, [fade]);

  return (
    <>
      <Animated.View
        style={{
          opacity: fade,
          marginTop: 16,
          gap: 12,
        }}
      >
        {/* Countdown card — header, progress ring, and the record/chat
          actions. Stands on its own on the screen background; the reassurance
          and learn-while-you-wait content that used to sit below now lives on
          the home page, keeping Cycle Detail focused on cycle data and entry. */}
        <View
          style={{
            ...(GLASS_CARD_STYLE as Record<string, unknown>),
            borderRadius: 24,
            borderColor: "rgba(92,52,32,0.08)",
            padding: 22,
            gap: 22,
            shadowColor: "#000000",
            shadowOffset: { width: 0, height: 6 },
            shadowOpacity: 0.05,
            shadowRadius: 16,
            elevation: 4,
          }}
        >
          {/* Header — centered eyebrow + title with the test date inline
            (underlined + tappable, since it's an assumption of transfer + 14
            days the user can adjust). Generous line-height and spacing give
            the title room to breathe. */}
          <View style={{ alignItems: "center", paddingTop: 6 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 17,
                fontWeight: "800",
                lineHeight: 25,
                color: EMBER.colors.text,
                textAlign: "center",
                paddingHorizontal: 8,
              }}
            >
              {t(`${ns}.made_it_through`)}
              {/* With a date the two halves are one sentence ("…behind you, your
                  test will be on 12 February"). With none, the link is its own
                  sentence, so it needs a full stop rather than the bare space
                  that read as a missing-punctuation typo. */}
              {testDate ? `, ${t(`${ns}.test_will_be_on`)} ` : ". "}
              <Text
                onPress={
                  onEditTestAppointment
                    ? () => {
                        capturePress(
                          pressLabel(
                            testDate
                              ? formatLongDate(testDate, locale)
                              : t(`${ns}.tap_to_pick_date`),
                          ),
                          "test_date_row",
                        );
                        onEditTestAppointment();
                      }
                    : undefined
                }
                suppressHighlighting
                testID="waiting-phase-test-date-row"
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                  ...(testDateIsEditable
                    ? { textDecorationLine: "underline" as const }
                    : {}),
                }}
              >
                {testDate
                  ? formatLongDate(testDate, locale)
                  : t(`${ns}.tap_to_pick_date`)}
              </Text>
            </Text>
          </View>
          {testDateSaveError ? (
            <View
              style={{
                gap: 10,
                padding: 12,
                borderRadius: 12,
                backgroundColor: "rgba(184,74,22,0.08)",
                borderWidth: 1,
                borderColor: "rgba(184,74,22,0.2)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.text,
                }}
              >
                {testDateSaveError}
              </Text>
              {onRetryTestDate ? (
                <CompactActionButton
                  variant="secondary"
                  label={t(`${ns}.retry_test_date`)}
                  onPress={onRetryTestDate}
                  loading={isSavingTestDate}
                />
              ) : null}
            </View>
          ) : null}
          {/* Circular progress ring */}
          <View style={{ alignItems: "center", gap: 12 }}>
            <ProgressRing
              progress={progress}
              size={156}
              strokeWidth={9}
              dayCurrent={dayCurrent}
              dayTotal={dayTotal}
              isTestDay={isTestDay}
              isPastTest={isPastTest}
              t={t}
            />
          </View>

          {/* Test-day / past-test announcement strip — replaces the dot
              track and primes the user for the Record CTA below. */}
          {dayCurrent !== null &&
          dayTotal !== null &&
          (isTestDay || isPastTest) ? (
            <View
              style={{
                borderRadius: 12,
                paddingVertical: 10,
                paddingHorizontal: 14,
                alignItems: "center",
                borderWidth: 1,
                borderColor: "rgba(92,52,32,0.08)",
                backgroundColor: "rgba(255,255,255,0.46)",
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {isTestDay
                  ? perspective === "partner"
                    ? t(`${ns}.test_day_partner_announce`)
                    : t(`${ns}.test_day_self_announce`)
                  : (() => {
                      const daysAgo = Math.max(
                        1,
                        daysBetween(testDate ?? "", today),
                      );
                      return daysAgo === 1
                        ? t(`${ns}.test_was_ago_one`)
                        : t(`${ns}.test_was_ago_other`, {
                            params: { count: String(daysAgo) },
                          });
                    })()}
              </Text>
            </View>
          ) : null}

          {/* Record beta result — the action that closes out the wait.
              Moved inside the card and rebuilt on the shared CompactActionButton
              (white / faint-border secondary, the same button the costs
              estimator cards use) instead of a bespoke pill. */}
          <View style={{ gap: 10, paddingTop: 4 }}>
            <View style={{ flexDirection: "row", gap: 10 }}>
              <CompactActionButton
                testID="record-beta-result-trigger"
                variant="secondary"
                // Always open here. The gate that could disable this read the
                // FET beta order, and this view is for the cycle types that
                // have none — it moved out with the order card.
                label={
                  perspective === "partner"
                    ? t(`${ns}.record_beta_partner`)
                    : t(`${ns}.record_beta_self`)
                }
                onPress={onRecordResult}
                loading={isLoadingResult}
                numberOfLines={2}
                style={{ flex: 1 }}
                leading={
                  isTestDay || isPastTest ? (
                    <Sparkles
                      size={15}
                      color={EMBER.colors.text}
                      strokeWidth={1.8}
                    />
                  ) : (
                    <Heart
                      size={14}
                      color={EMBER.colors.text}
                      strokeWidth={1.8}
                    />
                  )
                }
              />
              {onChatAboutThis ? (
                <CompactActionButton
                  testID="waiting-phase-chat-trigger"
                  variant="secondary"
                  label={t(`${ns}.chat_about_this`)}
                  onPress={onChatAboutThis}
                  numberOfLines={2}
                  style={{ flex: 1 }}
                  leading={
                    <MessageCircle
                      size={14}
                      color={EMBER.colors.text}
                      strokeWidth={1.8}
                    />
                  }
                />
              ) : null}
            </View>

            {/* Upload-photo affordance on test day / past-test. Reads as an
                upcoming feature: dashed border, camera glyph, and the shared
                StatusBadge "coming-soon" chip — same pattern used in
                PharmacyHeader, MoneySideSection, CompensationPlaceholderCard,
                etc. Wires to onUploadResult if provided, otherwise a friendly
                TODO alert. Real photo OCR is Phase 4b. */}
            {(isTestDay || isPastTest) && !isLoadingResult ? (
              <Pressable
                onPress={() => {
                  capturePress("upload_result_photo");
                  if (onUploadResult) {
                    onUploadResult();
                    return;
                  }
                  Alert.alert(
                    t(`${ns}.upload_coming_soon_title`),
                    t(`${ns}.upload_coming_soon_body`),
                  );
                }}
                className="flex-row items-center"
                style={({ pressed }) => ({
                  gap: 12,
                  paddingVertical: 14,
                  paddingHorizontal: 14,
                  borderRadius: 14,
                  backgroundColor: pressed
                    ? "rgba(255,255,255,0.5)"
                    : "rgba(255,255,255,0.28)",
                  borderWidth: 1,
                  borderStyle: "dashed",
                  borderColor: "rgba(92,52,32,0.18)",
                  opacity: 0.85,
                })}
              >
                <View
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    backgroundColor: "rgba(92,52,32,0.06)",
                    borderWidth: 1,
                    borderColor: "rgba(92,52,32,0.1)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Camera
                    size={17}
                    color={EMBER.colors.textMid}
                    strokeWidth={1.6}
                  />
                </View>
                <Text
                  className="font-dm-sans"
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    letterSpacing: -0.1,
                  }}
                >
                  {t(`${ns}.upload_test_photo`)}
                </Text>
                <StatusBadge type="coming-soon" tone="gold" shape="pill" />
              </Pressable>
            ) : null}
          </View>
        </View>
      </Animated.View>
    </>
  );
}

// ── ProgressRing ─────────────────────────────────────────────
// Circular progress indicator with the day number in the centre.
// The progress arc is painted with the shared care-hero glow texture (the same
// warm-orange image used by the care page hero and the active track of
// MoonToggle) via an SVG pattern, so the fill matches the rest of the app
// instead of a one-off brown gradient.

const RING_FILL_TEXTURE =
  require("@src/apps/moon/assets/images/care-hero-glow-gradient.png") as number;

function ProgressRing({
  progress,
  size,
  strokeWidth,
  dayCurrent,
  dayTotal,
  isTestDay,
  isPastTest,
  t,
}: {
  progress: number; // 0..1
  size: number;
  strokeWidth: number;
  dayCurrent: number | null;
  dayTotal: number | null;
  isTestDay: boolean;
  isPastTest: boolean;
  t: TranslationFunction;
}) {
  const ns = "care.treatment_hub.waiting_phase";
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const offset = circumference * (1 - clampedProgress);

  // Position of the leading edge of the progress arc, in screen coords.
  // Starts at 12 o'clock (top) because of the SVG's -90° rotation.
  const leadingAngle = clampedProgress * 2 * Math.PI - Math.PI / 2;
  const leadingX = cx + radius * Math.cos(leadingAngle);
  const leadingY = cy + radius * Math.sin(leadingAngle);

  // Milestone tick at 50% (~midway through the wait). Positioned the same
  // way as the leading edge but at fixed 0.5 progress.
  const midAngle = 0.5 * 2 * Math.PI - Math.PI / 2;
  const midX = cx + radius * Math.cos(midAngle);
  const midY = cy + radius * Math.sin(midAngle);

  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={size} height={size}>
        <Defs>
          {/* Care-hero glow texture as the arc fill (matches MoonToggle's
              active track). Covers the whole ring box so the arc samples the
              warm-orange image wherever it sweeps. */}
          <Pattern
            id="ringFill"
            patternUnits="userSpaceOnUse"
            x="0"
            y="0"
            width={size}
            height={size}
          >
            {/* Zoom hard into the single bright core of the glow (~50% x,
                ~48% y of the source) and stretch it across the whole ring box
                so the arc samples that bright orange uniformly, instead of the
                darker edges. preserveAspectRatio="none" gives exact control of
                which image point lands at box centre. */}
            <SvgImage
              href={RING_FILL_TEXTURE}
              x={-size * 2.0}
              y={-size * 1.9}
              width={size * 5}
              height={size * 5}
              preserveAspectRatio="none"
            />
          </Pattern>
          <SvgRadialGradient
            id="ringHalo"
            cx="50%"
            cy="50%"
            rx="50%"
            ry="50%"
            fx="50%"
            fy="50%"
          >
            <Stop offset="0" stopColor="rgba(92,52,32,0.12)" />
            <Stop offset="0.6" stopColor="rgba(92,52,32,0.04)" />
            <Stop offset="1" stopColor="rgba(92,52,32,0)" />
          </SvgRadialGradient>
        </Defs>

        {/* Soft halo behind the ring — gives depth, makes the ring feel
            embedded in the warm card rather than stamped on top. */}
        <Circle cx={cx} cy={cy} r={radius - 4} fill="url(#ringHalo)" />

        {/* Track + progress + leading-edge dot share a -90° rotation so the
            stroke starts at 12 o'clock and goes clockwise. */}
        <Circle
          cx={cx}
          cy={cy}
          r={radius}
          stroke="rgba(0,0,0,0.05)"
          strokeWidth={strokeWidth}
          fill="none"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
        <Circle
          cx={cx}
          cy={cy}
          r={radius}
          stroke="url(#ringFill)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          fill="none"
          transform={`rotate(-90 ${cx} ${cy})`}
        />

        {/* Mid-wait milestone tick — small dot at the halfway position.
            Sits on the track, not yet reached visually until progress
            crosses it. */}
        {dayTotal !== null && dayTotal > 1 ? (
          <Circle
            cx={midX}
            cy={midY}
            r={2.2}
            fill={
              clampedProgress >= 0.5 ? EMBER.colors.textMid : "rgba(0,0,0,0.2)"
            }
            opacity={clampedProgress >= 0.5 ? 0.9 : 0.6}
          />
        ) : null}

        {/* Leading-edge dot — pulsing position marker at the head of the
            progress arc. Hidden when there's no progress to show. */}
        {clampedProgress > 0 && clampedProgress < 1 ? (
          <>
            <Circle
              cx={leadingX}
              cy={leadingY}
              r={strokeWidth / 2 + 4}
              fill="rgba(92,52,32,0.18)"
            />
            <Circle
              cx={leadingX}
              cy={leadingY}
              r={strokeWidth / 2}
              fill={EMBER.colors.textMid}
            />
          </>
        ) : null}

        {/* Test-day completion star — full ring done, sits at the top. */}
        {clampedProgress >= 1 ? (
          <Circle
            cx={cx}
            cy={strokeWidth / 2}
            r={6}
            fill={EMBER.colors.textMid}
          />
        ) : null}
      </Svg>

      <View
        style={{
          position: "absolute",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {dayCurrent !== null && dayTotal !== null ? (
          <>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "800",
                color: isTestDay
                  ? EMBER.colors.textMid
                  : EMBER.colors.textFaint,
              }}
            >
              {isTestDay
                ? t(`${ns}.test_day_label`)
                : isPastTest
                  ? t(`${ns}.test_passed_label`)
                  : t(`${ns}.day_label`)}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 36,
                fontWeight: "500",
                color: EMBER.colors.text,
                lineHeight: 38,
              }}
            >
              {dayCurrent}
            </Text>
            <Text
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: EMBER.colors.textFaint,
              }}
            >
              {t(`${ns}.of_total`, { params: { total: String(dayTotal) } })}
            </Text>
          </>
        ) : (
          <Text
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.textMid,
              textAlign: "center",
              paddingHorizontal: 16,
            }}
          >
            {t(`${ns}.pick_test_date`)}
          </Text>
        )}
      </View>
    </View>
  );
}
